import { useEffect, useState } from 'react';
import { SEO } from '../components/SEO';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fallbackTeam = [
  {
    id: '1',
    name: 'Leo',
    role: 'Founder & Creative Director',
    bio: 'The one who turns ideas into events.',
    image: '/team/team-1.webp',
  },
  {
    id: '2',
    name: 'Sonya',
    role: 'Operations Manager',
    bio: 'Keeping the chaos organised.',
    image: '/team/team-2.webp',
  },
  {
    id: '3',
    name: 'Tracy',
    role: 'Entertainment Director',
    bio: 'Everything entertainment.',
    image: '/team/team-4.webp',
  },
  {
    id: '4',
    name: 'Tiaan',
    role: 'Technical Director',
    bio: 'I work with technology.',
    image: '/team/team-7.jpg',
  },
  {
    id: '5',
    name: 'Leán',
    role: 'Creative Genius',
    bio: 'I design, shoot, and edit.',
    image: '/team/team-5.webp',
  },
  {
    id: '6',
    name: 'Saydi Barbosa',
    role: 'Event Coordinator',
    bio: 'I take the show on the road.',
    image: '/team/team-6.jpg',
  },
];

// Shared with scripts/generate-routes.js, which turns these into HowTo schema.
import processSteps from '@/data/process.json';

export const AboutPage = () => {
  const heroRef = useRef(null);
  const positioningRef = useRef(null);
  const processRef = useRef(null);
  const philosophyRef = useRef(null);
  const teamRef = useRef(null);

  const isHeroInView = useInView(heroRef, { once: true });
  const isPositioningInView = useInView(positioningRef, { once: true, margin: '-100px' });
  const isProcessInView = useInView(processRef, { once: true, margin: '-100px' });
  const isPhilosophyInView = useInView(philosophyRef, { once: true, margin: '-100px' });
  const isTeamInView = useInView(teamRef, { once: true, margin: '-100px' });

  const [team, setTeam] = useState(fallbackTeam);

  useEffect(() => {
    window.scrollTo(0, 0);

    const fetchTeam = async () => {
      try {
        if (!BACKEND_URL) return;
        const response = await axios.get(`${API}/team`);
        if (Array.isArray(response.data) && response.data.length > 0) {
          setTeam(response.data);
        }
      } catch (error) {
        console.log('Using fallback team data');
      }
    };
    fetchTeam();
  }, []);

  return (
    <main id="main-content" tabIndex={-1} data-testid="about-page">
      <SEO
        title="About Us — Boutique Event Company Pretoria"
        description="Meet the team behind New Creative Industries — a boutique Pretoria event company built on quality over quantity, and built around your vision."
        path="/about"
        image="/brand/about-hero.jpg"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'About', path: '/about' },
        ]}
      />
      {/* Hero Banner */}
      <section
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center justify-center overflow-hidden pt-24"
      >
        <div className="absolute inset-0">
          <img
            src="/brand/about-hero.jpg"
            alt="New Creative Industries event production"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0D0D0D]/70 via-[#0D0D0D]/50 to-[#0D0D0D]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.3em] text-[#47debd] font-bold mb-4"
          >
            About New Creative Industries
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-none text-white"
          >
            Not Built for Volume.
            <br />
            <span className="text-[#47c3d6]">Built for Impact.</span>
          </motion.h1>
        </div>
      </section>

      {/* Positioning — Who We Are */}
      <section ref={positioningRef} className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isPositioningInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#17798A] font-bold mb-4">
                Who We Are
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#111111] mb-6">
                A boutique event company built on one principle.
              </h2>
              <p className="text-[#666666] text-base leading-relaxed">
                New Creative Industries is a boutique event experience company built on quality
                over quantity. We work with a select group of clients to create events that feel
                considered, intentional, and aligned — not rushed, templated, or transactional.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isPositioningInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="space-y-6"
            >
              <p className="text-[#666666] text-base leading-relaxed">
                Our work sits at the intersection of event organising, creative direction, and
                technical execution. Every project is approached with clarity, structure, and
                attention to detail.
              </p>
              <p className="text-[#666666] text-base leading-relaxed">
                We are a full-service event company — from concept and décor through to
                entertainment, technical production, catering, and on-site coordination. When
                you work with us, one team manages the whole picture.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section ref={processRef} className="py-24 md:py-32 bg-[#F5F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.3em] text-[#17798A] font-bold mb-4"
            >
              Our Process
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl font-bold tracking-tight text-[#111111]"
            >
              How We Work
            </motion.h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 40 }}
                animate={isProcessInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="bg-white border border-[#E5E9ED] p-8 md:p-10 rounded-xl shadow-sm"
                data-testid={`process-step-${index}`}
              >
                <div className="w-12 h-12 bg-[#47c3d6]/10 border border-[#47c3d6]/30 rounded flex items-center justify-center mb-6">
                  <span className="text-xl font-bold text-[#17798A]">{step.number}</span>
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-4">{step.title}</h3>
                <p className="text-[#666666] text-sm leading-relaxed">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section ref={philosophyRef} className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isPhilosophyInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.3em] text-[#17798A] font-bold mb-8"
            >
              Our Philosophy
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={isPhilosophyInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-[#111111] leading-tight mb-12"
            >
              We are not built for volume.
              <br />
              <span className="text-[#2A93A6]">We are built for impact.</span>
            </motion.h2>
            <div className="grid md:grid-cols-2 gap-8">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isPhilosophyInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-[#666666] text-base leading-relaxed"
              >
                Every event we take on receives the same level of attention, regardless of scale.
                We don't carry a high volume of projects at once — because doing so would
                compromise the quality and focus that our clients expect.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isPhilosophyInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-[#666666] text-base leading-relaxed"
              >
                A well-executed event is the result of deliberate planning, honest communication,
                and a clear understanding of what success looks like for the client. That is the
                standard we hold ourselves to — every time.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section ref={teamRef} className="py-24 md:py-32 bg-[#F5F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isTeamInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs uppercase tracking-[0.3em] text-[#17798A] font-bold mb-4"
            >
              The People Behind the Work
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isTeamInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight text-[#111111]"
            >
              Meet Our <span className="text-[#2A93A6]">Team</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 40 }}
                animate={isTeamInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="group"
                data-testid={`team-member-${index}`}
              >
                <div className="relative aspect-square overflow-hidden mb-6 bg-[#151515] rounded-xl">
                  <img
                    src={member.image}
                    alt={`${member.name} — ${member.role} at New Creative Industries`}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-transparent to-transparent opacity-60" />
                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#47c3d6] transition-colors duration-300" />
                </div>
                <h3 className="text-xl font-bold text-[#111111] mb-2 group-hover:text-[#17798A] transition-colors">
                  {member.name}
                </h3>
                <p className="text-[#666666] text-sm leading-relaxed">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#111111] mb-6">
            Let's Start With a Conversation
          </h2>
          <p className="text-[#666666] max-w-xl mx-auto mb-8">
            Tell us about your event. We'll take it from there.
          </p>
          <Link
            to="/#book"
            data-testid="about-cta"
            className="inline-flex items-center gap-3 px-10 py-5 text-sm font-bold uppercase tracking-widest text-black bg-[#47debd] hover:bg-white transition-all duration-300 group rounded-lg"
          >
            Get In Touch
            <ArrowRight
              size={18}
              className="group-hover:translate-x-1 transition-transform"
            />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
